/**
 * Générateur — Identifier si une situation relève du modèle de la proportionnalité
 * Attendu ID : 683 | Niveau : 6ème | Domaine : Proportionnalité (cluster 41)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - tableau_prop_qcm     : QCM 4 choix — lire un tableau et décider proportionnel/non
 *   - situation_qcm        : QCM 4 choix — une situation narrative : prop ou non
 *   - proprietes_vf        : V/F 6 lignes (3V + 3F)
 *   - classer_situations   : classer 4 situations en 2 catégories (prop / non prop)
 *   - coefficient_saisie   : saisie du coefficient d'un tableau proportionnel
 *   - contre_exemple_qcm   : QCM 4 choix — identifier la colonne qui brise la prop.
 *
 * Spécificité : 100 % texte (HTML natif, pas de SVG). Les tableaux sont des <table>
 * HTML inline dans l'énoncé.
 *
 * Partition avec attendus voisins :
 *   - 682 : définition + expressions de la vie courante.
 *   - 684 : procédures de résolution (linéarité, retour à l'unité multi-étapes).
 *   - 685 : tableau de proportionnalité / notations symboliques (flèches).
 *
 * Cible : 20/20 uniques sur 20 itérations, 5 runs consécutifs minimum.
 */

import {
    randomChoice, shuffle, randInt,
    makeQcm, makeSaisie, makeVraiFaux, makeClasser,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 683;

const K_POOL = [1.5, 2, 2.5, 3, 4, 5, 0.8];
const K_POOL_ENTIER = [2, 3, 4, 5];

const L_TAB   = 'Lecture d\'un tableau : proportionnel ou non (QCM)';
const L_SIT   = 'Situation narrative : proportionnelle ou non (QCM)';
const L_VF    = 'Propriétés du modèle de proportionnalité (V/F)';
const L_CLS   = 'Classer des situations (prop. / non prop.)';
const L_COEF  = 'Trouver le coefficient d\'un tableau (saisie)';
const L_CEX   = 'Contre-exemple : quelle colonne trahit la non-proportionnalité (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_TAB,
});


// Formatte un nombre décimal avec une virgule FR, sans zéros traînants.
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    const s = Number(n).toFixed(2);
    const trimmed = s.replace(/\.?0+$/, '');
    return trimmed.replace('.', ',');
};

// Helpers table HTML
// Valeurs en <td> sur les DEUX lignes (revue 2026-08-10) : la 1ʳᵉ ligne en
// <th> et la 2ᵉ en <td> rendaient les valeurs désalignées à l'écran — seuls
// les intitulés de ligne restent en <th>.
const buildTable = (headerA, headerB, col1A, col1B, col2A, col2B, col3A, col3B) => {
    return `<table><tbody><tr><th>${headerA}</th><td>${col1A}</td><td>${col2A}</td><td>${col3A}</td></tr>` +
        `<tr><th>${headerB}</th><td>${col1B}</td><td>${col2B}</td><td>${col3B}</td></tr></tbody></table>`;
};

// Pas de ligne de numérotation (décision utilisateur 2026-08-10, revenant sur
// l'ajout du jour) : l'élève doit se familiariser avec le tableau standard.
const buildTable4 = (headerA, headerB, valuesA, valuesB) => {
    const tdA = valuesA.map(v => `<td>${v}</td>`).join('');
    const tdB = valuesB.map(v => `<td>${v}</td>`).join('');
    return `<table><tbody><tr><th>${headerA}</th>${tdA}</tr>` +
        `<tr><th>${headerB}</th>${tdB}</tr></tbody></table>`;
};

// Contextes partagés pour les tableaux (lignes 1 et 2)
const TABLE_CONTEXTS = [
    { headerA: 'Masse (kg)',      headerB: 'Prix (€)',     label: 'prix au kilo' },
    { headerA: 'Durée (h)',       headerB: 'Distance (km)', label: 'vitesse constante' },
    // entier : la 2ᵉ ligne est un comptage — coefficient décimal interdit
    // (revue 2026-08-10 : « 4,5 pages » dans 4,1 % des tirages).
    { headerA: 'Durée (min)',     headerB: 'Pages',        label: 'impression', entier: true },
    { headerA: 'Durée (min)',     headerB: 'Volume (L)',   label: 'débit' },
    { headerA: 'Nombre d\'objets', headerB: 'Prix (€)',    label: 'prix à l\'unité' },
];

// Triplets de valeurs ligne 1 (croissantes, < 10 pour lisibilité)
const TRIPLETS_ENTIERS = [
    [2, 3, 4],
    [3, 4, 5],
    [4, 5, 6],
    [2, 4, 5],
    [2, 3, 5],
    [3, 5, 6],
    [4, 6, 8],
    [2, 5, 10],
];

// ─── Format 1 — Tableau proportionnel ou non (QCM) ───────────

const JUSTIFS_PROP_OK = [
    'Oui, car on multiplie toujours par le même nombre pour passer de la première ligne à la deuxième.',
    'Oui, car les rapports entre les deux grandeurs sont tous égaux.',
    'Oui, car il existe un coefficient constant entre les deux grandeurs.',
];

const JUSTIFS_NONPROP_OK = [
    'Non, car les rapports entre les deux grandeurs ne sont pas tous égaux.',
    'Non, car on ne multiplie pas toujours par le même nombre.',
    'Non, car il n\'y a pas de coefficient constant entre les deux grandeurs.',
];

// Distracteurs en longueurs encadrantes (revue 2026-08-10, recette gen 682) :
// « coche la plus longue » gagnait dans 60,8 % des tirages.
const DISTRACTEURS_PROP = [
    'Non, car les deux grandeurs augmentent donc elles ne peuvent pas être proportionnelles.',
    'Non, car les nombres du tableau ne sont pas tous entiers.',
    'Oui, car les deux grandeurs augmentent en même temps.',
    'Non, car les valeurs de la deuxième ligne ne sont pas égales à celles de la première.',
];

const DISTRACTEURS_NONPROP = [
    'Oui, car les deux grandeurs augmentent en même temps.',
    'Oui, car toutes les valeurs du tableau sont différentes les unes des autres.',
    'Non, car on additionne toujours le même nombre pour passer d\'une ligne à l\'autre.',
    'Oui, car la deuxième ligne est plus grande que la première.',
];

const genTableauPropQcm = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    const triplet = randomChoice(TRIPLETS_ENTIERS);
    const isProp = Math.random() < 0.5;

    let rowB, bonne, distracteursPool;

    if (isProp) {
        const k = randomChoice(ctx.entier ? K_POOL_ENTIER : K_POOL);
        rowB = triplet.map(v => fmt(v * k));
        bonne = randomChoice(JUSTIFS_PROP_OK);
        distracteursPool = DISTRACTEURS_PROP;
    } else {
        // 3 sous-types : additif, incohérent, quadratique
        const subType = randInt(0, 2);
        // Sur le sous-type ADDITIF (revue 2026-08-10), « Non, car on
        // additionne toujours le même nombre » est un verdict juste ET une
        // observation exacte du tableau — l'élève qui le cochait était compté
        // faux. On l'écarte du pool pour ces tirages uniquement.
        const distracteursSurs = subType === 0
            ? DISTRACTEURS_NONPROP.filter(d => !/additionne toujours/.test(d))
            : DISTRACTEURS_NONPROP;
        if (subType === 0) {
            // Additif : ligne 2 = ligne 1 + c (c ∈ [2, 5])
            const c = randInt(2, 5);
            rowB = triplet.map(v => fmt(v + c));
        } else if (subType === 1) {
            // Incohérent : k sur 2 colonnes sauf 1 où k' ≠ k
            const k = randomChoice(K_POOL_ENTIER);
            const posErr = randInt(0, 2);
            // Décalage ±1 ou ±2 sur la colonne erronée
            const sign = Math.random() < 0.5 ? 1 : -1;
            const delta = sign * randomChoice([1, 2]);
            rowB = triplet.map((v, i) => {
                const base = v * k;
                return fmt(i === posErr ? base + delta : base);
            });
        } else {
            // Quadratique : ligne 2 = (ligne 1)²
            rowB = triplet.map(v => fmt(v * v));
        }
        bonne = randomChoice(JUSTIFS_NONPROP_OK);
        distracteursPool = distracteursSurs;
    }

    const rowA = triplet.map(v => fmt(v));
    const table = buildTable(
        ctx.headerA, ctx.headerB,
        rowA[0], rowB[0],
        rowA[1], rowB[1],
        rowA[2], rowB[2],
    );

    const question = `<p>Le tableau ci-dessous met en relation deux grandeurs.</p>${table}<p>Ces deux grandeurs sont-elles proportionnelles ?</p>`;
    const distracteurs = shuffle(distracteursPool).slice(0, 3);
    const choix = [bonne, ...distracteurs];

    const payload = makeQcm(choix, bonne, question, opts(L_TAB));
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-10) : vérifier les
    // rapports peut demander des divisions décimales (k = 1,5 ; 2,5 ; 0,8).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'tableau_prop_qcm';
    return payload;
};

// ─── Format 2 — Situation narrative (QCM) ────────────────────

const PROP_SITUATIONS = [
    () => {
        const p = randomChoice([1.20, 1.50, 1.80, 2.50, 3.00]);
        return `Un boulanger vend du pain au poids : 1 kg coûte ${fmt(p)} €, sans variation.`;
    },
    () => {
        const v = randomChoice([60, 70, 80, 90, 100]);
        return `Une voiture roule à la vitesse constante de ${v} km/h sur autoroute.`;
    },
    () => {
        const d = randomChoice([10, 12, 15, 20, 25]);
        return `Une imprimante imprime ${d} pages par minute, sans pause et sans bourrage.`;
    },
    () => {
        const s = randomChoice([12, 14, 15, 18, 20]);
        return `Un ouvrier est payé ${s} € par heure de travail effective.`;
    },
    () => {
        const q = randomChoice([2, 3, 4, 5]);
        return `Un robinet débite ${q} L d'eau par minute, à débit constant.`;
    },
    () => {
        const m = randomChoice([15, 17, 18, 20]);
        return `Chaque marche d'un escalier régulier a exactement ${m} cm de hauteur.`;
    },
    () => {
        const p = randomChoice([0.80, 1.20, 1.50, 2.00]);
        return `Des pots de yaourt identiques coûtent ${fmt(p)} € l'unité.`;
    },
    () => {
        const n = randomChoice([3, 4, 5, 6]);
        return `Un vélo parcourt ${n} mètres à chaque tour de pédalier régulier.`;
    },
];

const NONPROP_SITUATIONS = [
    () => 'La taille d\'un enfant en fonction de son âge, de la naissance à 15 ans.',
    () => 'Le prix d\'un paquet de gâteaux en promotion "le deuxième à moitié prix".',
    () => 'L\'aire d\'un carré en fonction de la longueur de son côté.',
    () => {
        const d = randInt(2, 7);
        return `L'âge de Paul et l'âge de Jules, sachant que Paul a toujours ${d} ans de plus que Jules.`;
    },
    () => 'La température extérieure en fonction de l\'heure de la journée.',
    () => 'La hauteur d\'eau dans une baignoire qu\'on remplit entièrement puis qu\'on vide.',
    () => {
        const a = randomChoice([10, 12, 15]);
        const b = randomChoice([2, 3]);
        return `Un abonnement de ${a} € par mois, auquel s'ajoute ${b} € par séance de sport pratiquée.`;
    },
    () => 'Le nombre de voitures passant à un péage entre 7 h et 7 h 15, puis entre 7 h et 7 h 30.',
];

// Refonte (revue 2026-08-10) : les méta-distracteurs (« cela dépend de la
// personne qui observe »…) étaient éliminables sans maths — le QCM était un
// pile ou face à 50 %. Désormais 4 choix justifiés : verdict × raison.
const JUSTIFS_SIT_OUI_OK = [
    'Oui, car on passe d\'une grandeur à l\'autre en multipliant toujours par le même nombre.',
    'Oui, car il existe un coefficient constant entre les deux grandeurs.',
];
const JUSTIFS_SIT_NON_OK = [
    'Non, car il n\'y a pas de coefficient constant entre les deux grandeurs.',
    'Non, car on ne passe pas d\'une grandeur à l\'autre en multipliant par un même nombre.',
];
const JUSTIFS_SIT_OUI_FAUX = [
    'Oui, car les deux grandeurs augmentent en même temps.',
    'Oui, car les deux grandeurs sont liées dans la vie courante.',
];
const JUSTIFS_SIT_NON_FAUX = [
    'Non, car les valeurs ne sont pas des nombres entiers.',
    'Non, car les deux grandeurs n\'ont pas la même unité.',
];

const construireSituationQcm = () => {
    const isProp = Math.random() < 0.5;
    const situation = isProp
        ? randomChoice(PROP_SITUATIONS)()
        : randomChoice(NONPROP_SITUATIONS)();
    const ouiOk = randomChoice(JUSTIFS_SIT_OUI_OK);
    const nonOk = randomChoice(JUSTIFS_SIT_NON_OK);
    const bonne = isProp ? ouiOk : nonOk;
    const choix = [
        ouiOk,
        nonOk,
        randomChoice(JUSTIFS_SIT_OUI_FAUX),
        randomChoice(JUSTIFS_SIT_NON_FAUX),
    ];

    const texte = `<p>${situation}</p><p>Les deux grandeurs évoquées sont-elles proportionnelles ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_SIT));
    payload._meta.generatorKey = 'situation_qcm';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-10, recette gen 682) : 197
// tirages sur 6000 servaient deux fois de suite le même exercice.
// Signature = énoncé SEUL : deux tirages au même énoncé avec des
// justifications différentes restent « la même question » pour l'élève.
let derniereSignatureSituation = null;
const signatureSituation = (p) => p.texte;

const genSituationQcm = () => {
    let payload = construireSituationQcm();
    for (let tries = 0; tries < 12 && signatureSituation(payload) === derniereSignatureSituation; tries++) {
        payload = construireSituationQcm();
    }
    derniereSignatureSituation = signatureSituation(payload);
    return payload;
};

// ─── Format 3 — Propriétés (V/F) ─────────────────────────────

// Clés sémantiques (revue 2026-08-10, recette gens 675-682) : jamais deux
// affirmations sur le même objet — « contrôler toutes les colonnes » (V) et
// « il suffit d'en vérifier deux » (F) tombaient ensemble dans 13 % des
// tirages.
const BUILDERS_V_683 = [
    { k: 'prix_masse', build: () => 'Le prix à payer est proportionnel à la masse achetée si le prix au kilo est fixe.' },
    { k: 'calc_prix', build: ({ p, k }) => `Dans un tableau où 1 kg coûte ${fmt(p)} € et ${k} kg coûtent ${fmt(p * k)} €, les deux grandeurs sont proportionnelles.` },
    { k: 'vitesse', build: () => 'La distance parcourue à vitesse constante est proportionnelle à la durée du trajet.' },
    { k: 'carre', build: () => 'Le périmètre d\'un carré est proportionnel à la longueur de son côté.' },
    { k: 'calc_robinet', build: ({ q, t }) => `Un robinet qui débite ${q} L par minute remplit ${q * t} L en ${t} minutes : c'est bien une situation de proportionnalité.` },
    { k: 'rapports', build: () => 'Pour qu\'un tableau soit proportionnel, il faut que tous les rapports entre les deux lignes soient égaux.' },
    { k: 'verif', build: () => 'Pour vérifier si un tableau est proportionnel, il faut contrôler toutes les colonnes, pas seulement deux.' },
    { k: 'salaire', build: () => 'Un salaire payé à l\'heure (taux horaire fixe) est proportionnel au nombre d\'heures travaillées.' },
];

const BUILDERS_F_683 = [
    { k: 'taille_age', build: () => 'La taille d\'un enfant est proportionnelle à son âge tout au long de sa croissance.' },
    // ⚠ même clé « carre » que le périmètre côté V : le vrai « périmètre
    // proportionnel » et le faux « aire proportionnelle » dans un même tirage
    // formeraient un duo trop guidant.
    { k: 'carre', build: () => 'L\'aire d\'un carré est proportionnelle à la longueur de son côté.' },
    { k: 'age_ecart', build: ({ d }) => `Paul a ${d} ans de plus que Jules, donc leurs âges sont proportionnels.` },
    { k: 'covariation', build: () => 'Dès que deux grandeurs augmentent en même temps, elles sont nécessairement proportionnelles.' },
    { k: 'verif', build: () => 'Pour savoir si un tableau est proportionnel, il suffit de vérifier deux colonnes sur trois.' },
    { k: 'additif', build: ({ c }) => `Un tableau où la deuxième ligne est toujours égale à la première ligne plus ${c} est proportionnel.` },
    { k: 'abonnement', build: () => 'Un abonnement fixe de 10 € par mois plus 2 € la séance rend le prix proportionnel au nombre de séances.' },
    { k: 'temperature', build: () => 'La température extérieure est proportionnelle à l\'heure de la journée.' },
];

const genProprietesVF = () => {
    const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;

    for (let attempt = 0; attempt < 8; attempt++) {
        const p = randomChoice([1.20, 1.50, 1.80, 2.00, 2.50, 3.00]);
        const k = randomChoice([2, 3, 4, 5]);
        const q = randomChoice([2, 3, 4, 5]);
        const t = randomChoice([2, 3, 4, 5, 6]);
        const ctxV = { p, k, q, t };
        const ctxF = { d: randInt(2, 7), c: randInt(2, 5) };

        // Quota variable (revue 2026-08-10) : le 3 V / 3 F constant se
        // déduisait par comptage. Sélection à clés sémantiques distinctes.
        const nbVrais = randInt(2, 4);
        const nbFaux = 6 - nbVrais;
        const seenKeys = new Set();
        const pick = (pool, n, ctx) => {
            const out = [];
            for (const b of shuffle(pool)) {
                if (out.length >= n) break;
                if (!seenKeys.has(b.k)) {
                    seenKeys.add(b.k);
                    out.push(b.build(ctx));
                }
            }
            return out;
        };
        const lignesV = pick(BUILDERS_V_683, nbVrais, ctxV);
        const lignesF = pick(BUILDERS_F_683, nbFaux, ctxF);
        if (lignesV.length < nbVrais || lignesF.length < nbFaux) continue;

        const combined = shuffle([
            ...lignesV.map(l => ({ ligne: l, br: 0 })),
            ...lignesF.map(l => ({ ligne: l, br: 1 })),
        ]);

        const lignes = combined.map(x => x.ligne);
        const bonnesReponses = combined.map(x => x.br);

        if (new Set(lignes).size === lignes.length) {
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'proprietes_vf';
            return payload;
        }
    }

    // Fallback
    const fallbackLignes = [
        'Le prix à payer est proportionnel à la masse achetée si le prix au kilo est fixe.',
        'La distance parcourue à vitesse constante est proportionnelle à la durée du trajet.',
        'Le périmètre d\'un carré est proportionnel à la longueur de son côté.',
        'La taille d\'un enfant est proportionnelle à son âge tout au long de sa croissance.',
        'L\'aire d\'un carré est proportionnelle à la longueur de son côté.',
        'Dès que deux grandeurs augmentent en même temps, elles sont nécessairement proportionnelles.',
    ];
    const fallbackBr = [0, 0, 0, 1, 1, 1];
    const order = shuffle([0, 1, 2, 3, 4, 5]);
    const lignes = order.map(i => fallbackLignes[i]);
    const bonnesReponses = order.map(i => fallbackBr[i]);
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'proprietes_vf';
    return payload;
};

// ─── Format 4 — Classer situations (Classer) ─────────────────

const PROP_SITS_CLS = [
    () => { const p = randomChoice([1.20, 1.50, 2.00, 2.50]); return `Prix payé pour 1 kg, 2 kg, 3 kg de pommes à ${fmt(p)} € le kilo.`; },
    () => { const v = randomChoice([50, 60, 70, 80]); return `Distance parcourue en 1 h, 2 h, 3 h à ${v} km/h constant.`; },
    () => { const s = randomChoice([12, 14, 15, 18]); return `Salaire pour 1 h, 2 h, 3 h de travail à ${s} € l'heure.`; },
    () => { const q = randomChoice([2, 3, 4, 5]); return `Volume d'eau écoulée en 1 min, 2 min, 3 min pour un débit de ${q} L par minute.`; },
    () => { const d = randomChoice([10, 12, 15, 20]); return `Nombre de pages imprimées en 1 min, 2 min, 3 min à ${d} pages par minute.`; },
    () => { const p = randomChoice([0.80, 1.20, 1.50, 2.00]); return `Prix pour 1, 2, 3 yaourts identiques à ${fmt(p)} € l'unité.`; },
    () => 'Périmètre d\'un carré pour un côté de 1 cm, 2 cm, 3 cm.',
    () => { const m = randomChoice([15, 17, 18, 20]); return `Hauteur totale d'un escalier pour 1, 2, 3 marches de ${m} cm chacune.`; },
];

const NONPROP_SITS_CLS = [
    () => 'Taille d\'un enfant à 1 an, 3 ans, 6 ans.',
    () => 'Aire d\'un carré pour un côté de 1 cm, 2 cm, 3 cm.',
    () => 'Température extérieure à 8 h, 12 h, 16 h un jour d\'hiver.',
    () => { const d = randInt(2, 6); return `Âge de Paul et âge de Jules, avec ${d} ans d'écart constant.`; },
    () => 'Prix d\'un paquet de gâteaux en promotion "1 paquet à 2 €, 2 paquets à 3 €".',
    () => 'Hauteur d\'eau dans une baignoire au cours du remplissage puis de la vidange.',
    () => { const a = randomChoice([10, 12, 15]); return `Prix pour 0, 1, 2 séances de sport avec un abonnement mensuel fixe de ${a} € plus 2 € la séance.`; },
    () => 'Nombre de voitures passant à un péage toutes les 15 minutes selon les heures de la journée.',
];

// Répartition variable + anti-réutilisation (revue 2026-08-10, décisions
// utilisateur) : le 2 + 2 constant se déduisait par comptage, et deux
// exercices consécutifs ne partagent plus aucune situation.
let precedentesSituationsCls = { prop: [], non: [] };

const genClasserSituations = () => {
    for (let attempt = 0; attempt < 6; attempt++) {
        const nbProp = randInt(1, 3);
        const nbNon = 4 - nbProp;
        const dispoProp = PROP_SITS_CLS.map((f, i) => i).filter(i => !precedentesSituationsCls.prop.includes(i));
        const dispoNon = NONPROP_SITS_CLS.map((f, i) => i).filter(i => !precedentesSituationsCls.non.includes(i));
        const idxProp = shuffle(dispoProp).slice(0, nbProp);
        const idxNon = shuffle(dispoNon).slice(0, nbNon);
        const propSits = idxProp.map(i => PROP_SITS_CLS[i]());
        const nonPropSits = idxNon.map(i => NONPROP_SITS_CLS[i]());
        const all = [...propSits, ...nonPropSits];
        if (new Set(all).size !== all.length) continue;

        precedentesSituationsCls = { prop: idxProp, non: idxNon };
        const categories = [
            { name: 'Proportionnelles', items: propSits },
            { name: 'Non proportionnelles', items: nonPropSits },
        ];

        const texte = `<p>Classe les situations suivantes selon qu'elles relèvent ou non d'une situation de proportionnalité.</p>`;
        const payload = makeClasser(categories, texte, opts(L_CLS));
        payload._meta.generatorKey = 'classer_situations';
        return payload;
    }

    // Fallback
    const propSits = ['Prix pour 1 kg, 2 kg, 3 kg de pommes à 2 € le kilo.', 'Périmètre d\'un carré pour un côté de 1 cm, 2 cm, 3 cm.'];
    const nonPropSits = ['Aire d\'un carré pour un côté de 1 cm, 2 cm, 3 cm.', 'Taille d\'un enfant à 1 an, 3 ans, 6 ans.'];
    const categories = [
        { name: 'Proportionnelles', items: propSits },
        { name: 'Non proportionnelles', items: nonPropSits },
    ];
    const texte = `<p>Classe les situations suivantes selon qu'elles relèvent ou non d'une situation de proportionnalité.</p>`;
    const payload = makeClasser(categories, texte, opts(L_CLS));
    payload._meta.generatorKey = 'classer_situations';
    return payload;
};

// ─── Format 5 — Coefficient (Saisie) ─────────────────────────

// Coefficients dont l'inverse est un décimal fini (revue 2026-08-10) : la
// variante « sens inverse » exclut 1,5 et 3 (1/3 = 0,333… non saisissable).
const K_POOL_INVERSIBLE = [2, 2.5, 4, 5, 0.8];
const K_POOL_INVERSIBLE_ENTIER = [2, 4, 5];

const genCoefficientSaisie = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    // Variante « sens inverse » (décision utilisateur 2026-08-10) : par quel
    // nombre multiplier la ligne du bas pour retrouver celle du haut (1/k).
    const sensInverse = Math.random() < 0.5;
    // Même garde qu'au format 1 (revue 2026-08-10) : pas de « 4,5 pages ».
    const k = sensInverse
        ? randomChoice(ctx.entier ? K_POOL_INVERSIBLE_ENTIER : K_POOL_INVERSIBLE)
        : randomChoice(ctx.entier ? K_POOL_ENTIER : K_POOL);
    const triplet = randomChoice(TRIPLETS_ENTIERS);

    const rowA = triplet.map(v => fmt(v));
    const rowB = triplet.map(v => fmt(v * k));
    const table = buildTable(
        ctx.headerA, ctx.headerB,
        rowA[0], rowB[0],
        rowA[1], rowB[1],
        rowA[2], rowB[2],
    );

    const question = sensInverse
        ? 'Par quel nombre faut-il multiplier la ligne du bas pour obtenir la ligne du haut ?'
        : 'Quel est le coefficient permettant de passer de la ligne du haut à la ligne du bas ?';
    const texte = `<p>Le tableau ci-dessous est un tableau de proportionnalité.</p>${table}<p>${question}</p>`;
    const reponse = sensInverse ? fmt(1 / k) : fmt(k);

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_COEF),
        strict: false,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-10) : trouver k
    // demande des divisions décimales (7,5 ÷ 3 ; 1,6 ÷ 2).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'coefficient_saisie';
    return payload;
};

// ─── Format 6 — Contre-exemple (QCM) ─────────────────────────

const genContreExempleQcm = () => {
    const ctx = randomChoice(TABLE_CONTEXTS);
    const k = randomChoice([2, 3, 4, 5]);

    // 4 valeurs distinctes dans [1, 10]
    const basePool = [1, 2, 3, 4, 5, 6, 7, 8, 10];
    const valuesA = shuffle(basePool).slice(0, 4).sort((a, b) => a - b);

    const posErr = randInt(0, 3);
    const sign = Math.random() < 0.5 ? 1 : -1;
    const delta = sign * randomChoice([1, 2]);

    const valuesB = valuesA.map((v, i) => {
        const base = v * k;
        return fmt(i === posErr ? base + delta : base);
    });
    const valuesAStr = valuesA.map(v => fmt(v));
    const table = buildTable4(ctx.headerA, ctx.headerB, valuesAStr, valuesB);

    const texte = `<p>Le tableau ci-dessous devrait être un tableau de proportionnalité, mais une colonne est erronée.</p>${table}<p>Quelle colonne montre que ce tableau n'est pas proportionnel ?</p>`;

    const choix = ['Colonne 1', 'Colonne 2', 'Colonne 3', 'Colonne 4'];
    const bonne = `Colonne ${posErr + 1}`;

    const payload = makeQcm(choix, bonne, texte, opts(L_CEX));
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-10) : vérifier
    // quatre rapports, comme aux formats 1 et 5.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'contre_exemple_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    tableau_prop_qcm:    genTableauPropQcm,
    situation_qcm:       genSituationQcm,
    proprietes_vf:       genProprietesVF,
    classer_situations:  genClasserSituations,
    coefficient_saisie:  genCoefficientSaisie,
    contre_exemple_qcm:  genContreExempleQcm,
};

export const CATEGORIES = [
    {
        id: 'proportionnalite_identification',
        label: 'Identifier si une situation relève du modèle de la proportionnalité',
        formatsList: [
            { key: 'tableau_prop_qcm',    label: L_TAB },
            { key: 'situation_qcm',       label: L_SIT },
            { key: 'proprietes_vf',       label: L_VF },
            { key: 'classer_situations',  label: L_CLS },
            { key: 'coefficient_saisie',  label: L_COEF },
            { key: 'contre_exemple_qcm',  label: L_CEX },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    K_POOL,
    TABLE_CONTEXTS,
    TRIPLETS_ENTIERS,
    PROP_SITUATIONS,
    NONPROP_SITUATIONS,
    BUILDERS_V_683,
    BUILDERS_F_683,
    PROP_SITS_CLS,
    NONPROP_SITS_CLS,
    fmt,
    buildTable,
    buildTable4,
    genTableauPropQcm,
    genSituationQcm,
    genProprietesVF,
    genClasserSituations,
    genCoefficientSaisie,
    genContreExempleQcm,
};
